import { useEffect, useReducer } from 'react';
import { Editor, EditorEvents } from '@tiptap/react';
import { TToolbarProps } from './types';
import { EditorSetupConfig } from 'screens/editorial/asset/utility/getEditorConfigs';

// Define type for the objects passed as parameters
export interface EditorBarStateProps {
  editor: Editor;
  toolbar: boolean;
  configs?: EditorSetupConfig;
}

type TToolbarStates = {
  isToolbarEnabled: boolean;
  activeEditor: Editor | null;
  toolbarBtns: TToolbarProps['buttons'];
};

export function useEditorBarState(editors: EditorBarStateProps[]) {
  const [{ isToolbarEnabled, activeEditor, toolbarBtns }, updateToolbarStates] =
    useReducer((a: TToolbarStates, b: TToolbarStates) => ({ ...a, ...b }), {
      isToolbarEnabled: false,
      activeEditor: null,
      toolbarBtns: [],
    });

  useEffect(() => {
    editors.forEach(({ editor, toolbar, configs }) => {
      if (!!editor) {
        // On Focus.
        editor.on('focus', () => {
          if (!!toolbar) {
            updateToolbarStates({
              isToolbarEnabled: true,
              activeEditor: editor,
              toolbarBtns: configs?.toolbarButtons,
            });
          } else {
            updateToolbarStates({
              isToolbarEnabled: false,
              activeEditor: editor,
              toolbarBtns: configs?.toolbarButtons,
            });
          }
        });

        // On Blur.
        editor.on('blur', ({ event }: EditorEvents['blur']) => {
          const target = event?.relatedTarget;

          if (!!toolbar) {
            // Check if the target is a child of the toolbar
            if (
              target &&
              ((target instanceof Element &&
                (target.closest('#toolbar-content') !== null ||
                  target.closest('#search-and-replace-menu') !== null)) ||
                (target instanceof Element &&
                  target.closest('.MuiToolbar-root') !== null))
            ) {
              updateToolbarStates({
                isToolbarEnabled: true,
                activeEditor: editor,
                toolbarBtns: configs?.toolbarButtons,
              });
            } else {
              updateToolbarStates({
                isToolbarEnabled: false,
                activeEditor: editor,
                toolbarBtns: configs?.toolbarButtons,
              });
            }
          } else {
            updateToolbarStates({
              isToolbarEnabled: false,
              activeEditor: editor,
              toolbarBtns: configs?.toolbarButtons,
            });
          }
        });
      }
    });

    return () => {
      editors.forEach(({ editor }) => {
        if (editor) {
          editor.off('focus');
          editor.off('blur');
        }
      });
    };
  }, [editors]);

  return { isToolbarEnabled, activeEditor, toolbarBtns };
}
